import React, { useState,useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Flex, Input, Row, Dropdown, Space, Table, Tooltip, Typography, theme, Menu, Spin, Modal, Tag} from "antd";
import { EditOutlined, MinusSquareOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons";
import { ConfirmationWrapper } from "../../../../../consent/helper";
import { SystemPurpose } from "../../../../../../records/us_privacy_regulation_records";
import { useDebouncedSearch } from "../../../../../../hooks";
import AddIabChoicesModal from "./AddIabChoicesModal";
import AddCustomChoicesModal from "./AddCustomChoices";
import { countryToRegionMap, GEO_LIST_TREE_STRUCTURE } from "../../../../../../constants";
import { getStepsDataFieldMap, updateStepsDataFunction } from "../../../../../common/SequentialNavigationWizard/SequentialLeftNav";
import { categorySortFunc, evaluateRequriedFieldsFromStep, IAB_CHOICES_SYSTEM_IDS, renderInfoTip, USNAT_CODE } from "../../../helper";
import { message } from "../../../../../../styleguide";
import AddChoicesModalForGlobalStandard from "../../../global_standard/AddChoicesModalForGlobalStandard";
import { generateRandom10DigitNumber } from "../../../../../utils";
import ReorderModal from "../../../../../common/ReorderModal";

const { Title, Text, Paragraph } = Typography;

const ChoiceSelection = (props) => {
  const { token } = theme.useToken();
  const [modal, contextHolder] = Modal.useModal();

  const [regulationCategories, setRegulationCategories] = useState(props.usPrivacyRegulation.categories ?? []);
  const systemPurposes = useSelector(state => state.usPrivacyReducerState.getIn(['purposes', 'value']));
  const systemPurposesPending = useSelector(state => state.usPrivacyReducerState.getIn(['purposes', 'pending']));
  const stateStringRegions = useSelector(state => state.usPrivacyReducerState.getIn(['stateStringRegions', 'value']))
  const systemPurposesJS = props.regulationCategoryOptionsCache?.filter(cat => cat.type == 'SYSTEM_PURPOSE').toJS() ?? [];
  const [systemPurposesWithValidConfigGeos, setSystemPurposesWithValidConfigGeos] = useState([]);
  const [cancelBtnTextAndOpenIabModal, setCancelBtnTextAndOpenIabModal] = useState(null)
  const [openGlobalStandardModal, setOpenGlobalStandardModal] = useState(null)
  const [openModalCustom, setOpenModalCustom] = useState(false)
  const [currentRecord, setCurrentRecord] = useState(null)
  const [searchValue, setSearchValue] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [reorderModalOpen, setReorderModalOpen] = useState(false)
  const stepsStatusMap = getStepsDataFieldMap(props.stepsData, 'status')

  const selectedIabPurposes = regulationCategories?.filter(pur => pur.type === "SYSTEM_PURPOSE").map(pur => pur.purposeRef.id)
  const filteredSystemPurpose = systemPurposesWithValidConfigGeos
    ?.filter(pur => !selectedIabPurposes.includes(pur.id))

  const checkForChangesDetectedErrorState = () => {
    if(props.readOnly) return;

    let updatedStepsData = props.stepsData;
    if(stepsStatusMap.get('language_translations') === 'finish') {
      updatedStepsData = updateStepsDataFunction(updatedStepsData, 'language_translations', 'warning', true)
    }
    if(stepsStatusMap.get('opt_in_opt_out') === 'finish') {
      updatedStepsData = updateStepsDataFunction(updatedStepsData, 'opt_in_opt_out', 'warning', true)
    }
    if(stepsStatusMap.get('vendor_management') === 'finish') {
      updatedStepsData = updateStepsDataFunction(updatedStepsData, 'vendor_management', 'warning', true)
    }
    if(stepsStatusMap.get('region_mapping') === 'finish') {
      updatedStepsData = updateStepsDataFunction(updatedStepsData, 'region_mapping', 'warning', true)
    }
    props.setStepsData((asdf) => updatedStepsData);
  }

  useEffect(() => {
    if(props.readOnly) return;
    props.usPrivacyRegulation.categories = regulationCategories;
    props.resetValidationError();
    
    const errorInLanguageTranslations = getStepsDataFieldMap(props.stepsData, 'error').get('language_translations');
    if(errorInLanguageTranslations && evaluateRequriedFieldsFromStep('language_translations', props.usPrivacyRegulation, [])){
      let updatedStepsData = updateStepsDataFunction(props.stepsData, 'language_translations', 'error', false)
      props.setStepsData((data) => updateStepsDataFunction(updatedStepsData, 'language_translations', 'status', 'finish')) 
    }
  }, [regulationCategories])

  useEffect(() => {
    if(props.readOnly || stepsStatusMap.get("choice_selection") === "finish") return;
    const timer = setTimeout(() => {
      if(props.isGlobalStandardFlow) {
        setOpenGlobalStandardModal("Skip for now")
      } else {
        setCancelBtnTextAndOpenIabModal("Skip for now");
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, []); 

  useEffect(() => {
    let selectedRegionCodes = []
    GEO_LIST_TREE_STRUCTURE.forEach((option) => {
      if (props.usPrivacyRegulation.appliesGeos.includes(option.key)) {
        const childCodes = option.children.map(c => c.code);
        const filteredChildCodes = childCodes.filter(code => !props.usPrivacyRegulation.exclusionGeos.includes(code));
        selectedRegionCodes = selectedRegionCodes.concat(filteredChildCodes);
      } else {
        const childCodes = option.children.map(c => c.code);
        const filteredChildCodes = childCodes.filter(code => props.usPrivacyRegulation.appliesGeos.includes(code) && !props.usPrivacyRegulation.exclusionGeos.includes(code));
        selectedRegionCodes = selectedRegionCodes.concat(filteredChildCodes)
      }
    });
    selectedRegionCodes = selectedRegionCodes.map((code) => code?.replace("STATE_", "US_"));
    const data = systemPurposesJS.map((cat) => {
      const configByGeo = Object.fromEntries(
        Object.entries(cat.configByGeo).filter(([key, value]) => (selectedRegionCodes.includes(key) && stateStringRegions[key]) || key === USNAT_CODE)
      );
      return ({id: cat.purposeRef.id, privacyChoice: cat.privacyChoice, configByGeo, systemId: cat.purposeRef ? cat.purposeRef.systemId : null})
    });
    setSystemPurposesWithValidConfigGeos(data);
  }, [systemPurposes]);

  const handleModalVisibility = (type) => {
    if (type === 'iab') {
      setCancelBtnTextAndOpenIabModal("Cancel")
    } else if (type === 'custom') {
      setOpenModalCustom(true)
      setCurrentRecord(null);
    } else if (type === 'globalStandard') {
      setOpenGlobalStandardModal("Cancel")
    }
  }

  const handleCancel = (type) => {
    if (type === 'iab') {
      setCancelBtnTextAndOpenIabModal(null)
    } else if (type === 'custom') {
      setOpenModalCustom(false);
      setCurrentRecord(null);
    } else if (type === 'globalStandard') {
      setOpenGlobalStandardModal(null);
    }
  }
  const addIabChoice = (selectedRows) => {
    return new Promise((resolve,reject) => {
      let choiceData = systemPurposesJS.filter(purpose => selectedRows.includes(purpose.purposeRef?.id));
      choiceData = choiceData.map((pur) => {
        // const configByGeo = systemPurposesWithValidConfigGeos.find((p) => p.id === pur.purposeRef?.id)?.configByGeo;
        const legislationConfigs = props.usPrivacyRegulation.configByGeo;
        // const usnatLegalBasis = pur.configByGeo[USNAT_CODE]?.defaultLegalBasis; //DIA-5735
        let updatedConfigByGeo = Object.fromEntries(Object.entries(legislationConfigs)
          .map(([key,value]) => {
          let newKey = key;
          if(props.isGlobalStandardFlow) {
            let parentKey = countryToRegionMap[key?.replace("US_", "STATE_")];
            if(parentKey?.includes("REGION_") ? legislationConfigs[key]?.legislation === "regional" : legislationConfigs[key]?.legislation === "usnat" ) {
              newKey = parentKey;
              value = pur.configByGeo[newKey] ?? {defaultLegalBasis: /*usnatLegalBasis ??*/ "NOT_APPLICABLE"};
            } else {
              value = pur.configByGeo[newKey] ?? pur?.configByGeo[newKey] ?? {defaultLegalBasis: "NOT_APPLICABLE"}
            }
          } else {
            if(value?.legislation === "usnat" && !props.isNoFrameworkFlow) {
              newKey = key?.split('_',1)[0];
              value = {defaultLegalBasis: /*usnatLegalBasis ??*/ "NOT_APPLICABLE"};
            } else if(stateStringRegions[key]) {
              value = pur.configByGeo[key] ?? {defaultLegalBasis : "NOT_APPLICABLE"};
            } else {
              value = {defaultLegalBasis : "NOT_APPLICABLE"}
            }
          }
          return [newKey,value]
        }))
        pur.configByGeo = updatedConfigByGeo;
        if (pur.identificationList) pur.identificationList = '';
        return pur;
      });
      setRegulationCategories((data) => [...data,...choiceData])
      setCancelBtnTextAndOpenIabModal(null)
      checkForChangesDetectedErrorState();
      resolve([...regulationCategories,...choiceData])
    })

  }

  const defaulStandardChoiceNames = ["Strictly Necessary","Performance Analytics", "Functionality or Personalization", "Targeted Advertising"];
  
  const addCustomChoice = async  (choiceName) => {
    let legislationConfigGeos = props.usPrivacyRegulation.configByGeo;
    let newPurposes = { privacyChoice: choiceName, _id: `custom_${generateRandom10DigitNumber()}`, type: "CUSTOM"};
    newPurposes.configByGeo = Object.fromEntries(Object.entries(legislationConfigGeos).map(([key,value]) => {
      let newValue = { defaultLegalBasis: "NOT_APPLICABLE"};
      let newKey = key;
      if(props.isGlobalStandardFlow) {
        let parentKey = countryToRegionMap[key?.replace("US_", "STATE_")];
        if(parentKey?.includes("REGION_") ? legislationConfigGeos[key]?.legislation === "regional" : legislationConfigGeos[key]?.legislation === "usnat" ) {
          newKey = parentKey;
        }
      } else if(legislationConfigGeos[key]?.legislation === "usnat" && !props.isNoFrameworkFlow) {
        newKey = key.split("_",1)[0];  
      }
      return [newKey, newValue];
    }))
    let updatedRegulationCategories = _.cloneDeep(props.usPrivacyRegulation.categories);
    let newCustomCategory = new SystemPurpose(newPurposes);
    updatedRegulationCategories.unshift(newCustomCategory.toJS());
    setRegulationCategories(updatedRegulationCategories)
    setOpenModalCustom(false)
    setCurrentRecord(null);
    checkForChangesDetectedErrorState();
  }

  const addStandardChoicesBulk = async (iabIds, choicesArray) => {
    let updatedRegulationCategories = await addIabChoice(iabIds);
    choicesArray.forEach(({privacyChoice, key}) => {
      let legislationConfigGeos = props.usPrivacyRegulation.configByGeo;
      let newPurposes = {
        privacyChoice: privacyChoice,
        _id: key,
        type: "CUSTOM",
        translations: {
          names : {
            en: privacyChoice,
          }
        }
      };
      newPurposes.configByGeo = Object.fromEntries(Object.entries(legislationConfigGeos).map(([key,value]) => {
        let newValue = { defaultLegalBasis: "NOT_APPLICABLE"};
        let newKey = key;
        if(props.isGlobalStandardFlow) {
          let parentKey = countryToRegionMap[key?.replace("US_", "STATE_")];
          if(parentKey?.includes("REGION_") ? legislationConfigGeos[key]?.legislation === "regional" : legislationConfigGeos[key]?.legislation === "usnat" ) {
            newKey = parentKey;
          }
        } else if(legislationConfigGeos[key]?.legislation === "usnat" && !props.isNoFrameworkFlow) {
          newKey = key.split("_",1)[0];  
        }  
        return [newKey, newValue];
      }))
      let newCustomCategory = new SystemPurpose(newPurposes);
      updatedRegulationCategories.unshift(newCustomCategory.toJS());
    });
    setRegulationCategories(updatedRegulationCategories)
    setOpenGlobalStandardModal(null)
  };

  const editCustomChoice = (choiceName, recordId) => {
    // let updatedRegulationCategories = _.cloneDeep(props.usPrivacyRegulation.categories);
    // let choiceIndex = updatedRegulationCategories.findIndex(reg => reg.id === record.id);
    // updatedRegulationCategories[choiceIndex].privacyChoice = choiceName
    setRegulationCategories((regCats) => 
      regCats.map((cat) => {
        if(cat.id === recordId) {
          cat.privacyChoice = choiceName
        }
        return cat
    }) 
    )
    props.setCustomPrivacyChoiceOptions((regCats) => 
      regCats.map((cat) => {
        if(cat.key === recordId) {
          cat.privacyChoice = choiceName
        }
        return cat
    }) )
    setOpenModalCustom(false)
    checkForChangesDetectedErrorState();
  }

  const menu = (
    <Menu>
      <Menu.Item disabled={filteredSystemPurpose.length === 0} title={filteredSystemPurpose.length === 0 ? "All IAB Choices have been added" : null} key="iab" onClick={() => handleModalVisibility('iab')}>{props.isNoFrameworkFlow ? "Standard" : "IAB"}</Menu.Item>
      <Menu.Item key="custom" onClick={() => handleModalVisibility('custom')}>Custom</Menu.Item>
    </Menu>
  );

  const handleDelete = (record) => {
    const updatedRegulationCategories = regulationCategories.filter(d => (d.id !== record.id));
    setSelectedRowKeys((selectedKeys) => selectedKeys.filter(key => key !== record.id))
    setRegulationCategories(updatedRegulationCategories)
  }

  const handleBulkDelete = async () => {
    const confirm = await modal.confirm({
      title: "Delete Choices",
      content: `Are you sure you want to delete ${selectedRowKeys?.length} Choices?`,
      width: 500,
    })
    if(confirm) {
      try {
        const updatedRegulationCategories = regulationCategories.filter(cat => !selectedRowKeys.includes(cat.id));
        setRegulationCategories(updatedRegulationCategories);
        message.success(selectedRowKeys?.length + " Privacy choices removed successfully");
        setSelectedRowKeys([])
      } catch(e) {
        console.log(e);
        message.error("Failed to remove Privacy choices");
      }
    }
  }

  const handleEditCustomCategory = (record) => {
    setCurrentRecord(record)
    setOpenModalCustom(true)
  }

 const SYSTEM_ID_TOOLTIP = props.isGlobalStandardFlow ? "A global ID that remains the same across all vendor lists. Custom privacy choices don't have system IDs because they vary for each vendor list." : "A global ID for IAB privacy choices that remains the same across all vendor lists. Custom privacy choices don't have system IDs because they vary for each vendor list.";

  const columns = [
    {
      title: "Privacy Choice",
      fixed: "left",
      dataIndex: "privacyChoice",
      width: 200,
      sorter: (a, b) => a.privacyChoice.localeCompare(b.privacyChoice),
      render: (privacyChoice, record) => <Paragraph ellipsis={{tooltip:{title: privacyChoice}, rows: 2}}>{record.privacyChoice ?? record.name}</Paragraph>
    },
    {
      title: <>Privacy Choice ID {renderInfoTip("A unique ID associated to privacy choices that is specific to this vendor list.")}</>,
      fixed: "left",
      dataIndex: "id",
      sorter: (a, b) => a.id.localeCompare(b.id),
      showSorterTooltip: false,
      width: 200,
    },
    {
      title: <>System ID {renderInfoTip(SYSTEM_ID_TOOLTIP)}</>,
      fixed: "left",
      dataIndex: "systemId",
      sorter: (a, b) => Number(a.systemId) - Number(b.systemId),
      showSorterTooltip: false,
      width: 200,
    }
  ];
    
  if(!props.isGlobalStandardFlow) {
    columns.push(
      {
        title: "Type",
        fixed: "left",
        dataIndex: "type",
        width: 200,
        filters: [
          {
            icon: <i className="fas fa-circle" style={{ color: '#FCA015', width: 10, height: 10, marginRight: 10 }} />,
            text: props.isNoFrameworkFlow ? "Standard" : "IAB",
            value: "IAB",
          },
          {
            icon: <i className="fas fa-circle" style={{ color: '#C7C7C7', width: 10, height: 10, marginRight: 10 }} />,
            text: "Custom",
            value: "CUSTOM",
          },
        ],
        onFilter: (value, record) => {
          let vendorType = "";
        if (record.type === "SYSTEM_PURPOSE") {
            vendorType = "IAB"
          } else {
            vendorType = "CUSTOM"
          }
          return value == vendorType
        },
        render: (value) => {
          return value === "SYSTEM_PURPOSE" ? props.isNoFrameworkFlow ? <Tag color="blue">Standard</Tag> : <Tag color="orange">IAB</Tag> : <Tag color="magenta">Custom</Tag>
        }
      }
    )
  }

  if (!props?.readOnly) {
    columns.push({
      title: "Actions",
      fixed: "right",
      width: 150,
      align: "center",
      render: (record) => (
        <>
          {record.type === "SYSTEM_PURPOSE" || (props.isGlobalStandardFlow && defaulStandardChoiceNames.includes(record.privacyChoice)) ? null : (<Tooltip title="Edit"><Button icon={<EditOutlined />} type="link" onClick={() => handleEditCustomCategory(record)} /></Tooltip>)}
          <ConfirmationWrapper
            actionDescription="delete this Choice"
            deleteHandler={() => handleDelete(record)}
            deleteBtn={<Button type="link" icon={<Tooltip title="Delete"><DeleteOutlined /></Tooltip>} />}
          />
        </>
      ),
    },)
  }

  const debouncedChangeHandler = useDebouncedSearch(setSearchValue, 700);

  let dataSource = searchValue.length ? _.cloneDeep(regulationCategories)?.filter(choice => choice.id?.toLowerCase().indexOf(searchValue) !== -1 || choice.privacyChoice.toLowerCase().indexOf(searchValue) !== -1) : _.cloneDeep(regulationCategories)
  dataSource = dataSource.map((record) => ({...record, key: record.id}));

  const rowSelection = {
    selectedRowKeys,
    onSelect : (record, selected, selectedRows, nativeEvent) => {
      if(selected) {
        setSelectedRowKeys((selectedKeys) => [... new Set(selectedKeys.concat(record.id))])
      } else {
        setSelectedRowKeys((selectedKeys) => selectedKeys.filter(key => key !== record.id))
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if(selected){
          setSelectedRowKeys((selectedKeys) => [...new Set(selectedKeys.concat(dataSource.map(v => v.id)))]);
      }else{
          setSelectedRowKeys((selectedKeys) => selectedKeys.filter((key) => !dataSource.map(v => v.id).includes(key)));
      }
    },
  }

  const disabledSellOrShare = props.usPrivacyRegulation.metaData.MspaCoveredTransaction && props.usPrivacyRegulation.metaData.MspaServiceProviderMode;

  return systemPurposesPending ? <Spin/> : (
    <>
      <Row gutter={[0, 16]}>
        {!props.readOnly ? (
          <Col span={24}>
            <Space direction="vertical" size={token.marginXXS}>
              <Title level={4}>Choice Selection</Title>
              <Text>Select the relevant data processing activities your company engages in</Text>
            </Space>
          </Col>
        ) : null}
        {regulationCategories.length > 0 && (
          <>
          <Col span={8}>
            <Input.Search
              placeholder="search"
              type="search"
              onChange={({ target: { value } }) => {
                  debouncedChangeHandler(value.trim().toLocaleLowerCase());
              }}
            />
          </Col>
          <Col span={8} offset={8}>
            {!props.readOnly ? (
              <Flex gap={token.margin} justify={"flex-end"}>
                {selectedRowKeys.length 
                ? (
                <>
                  <Button type="link" icon={<MinusSquareOutlined/>} onClick={() => setSelectedRowKeys([])} disabled={props.readOnly}> Clear Selection</Button>
                  {<Button type="primary" iconPosition="start" icon={<DeleteOutlined/>} onClick={handleBulkDelete} disabled={!selectedRowKeys.length || props.readOnly}> Bulk Delete ({selectedRowKeys.length})</Button>}
                </>
                ) : props.isGlobalStandardFlow
                ? (
                  <>
                  <Button onClick={() => setReorderModalOpen(true)}>
                    Reorder Choices
                  </Button>
                  <Button type="primary" onClick={() => handleModalVisibility('globalStandard')} disabled={props.readOnly}>Add Choice</Button>
                  </>
                ) : (
                <>
                  <Button onClick={() => setReorderModalOpen(true)}>
                    Reorder Choices
                  </Button>
                  <Dropdown.Button
                    style={{ width: "inherit" }}
                    icon={<DownOutlined />}
                    type="primary"
                    overlay={menu}
                    disabled={props.readOnly}
                  >
                    Add Choice
                  </Dropdown.Button>
                </>
                )}
              </Flex>
            ) : null}
          </Col>
          </>
        )}
        {regulationCategories.length === 0 && (
        <Col span={24}>
          {props.showError ? <Text type="danger">Select at least one Privacy Choice</Text> : null}
          <Flex flex="1" vertical align="center" justify="center" style={{ height: `500px`, background: 'white', border: props.showError ? "1px solid var(--ant-color-error)" : 0 }}>
            <Space direction="vertical" align="center" size={token.marginXXS}>
              <Typography.Title level={5} type="secondary">Start by adding your first privacy choice</Typography.Title>
              {props.isGlobalStandardFlow
              ? (
                <Button type="primary" onClick={() => handleModalVisibility('globalStandard')} disabled={props.readOnly}>Add Choice</Button>
              ) : (
                <Dropdown.Button style={{width: "inherit"}} icon={<DownOutlined />} type="primary" overlay={menu} disabled={props.readOnly}> Add Choice</Dropdown.Button>
              )}
            </Space>
          </Flex>
        </Col>
        )}
        {regulationCategories.length > 0 && (
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowSelection={!props.readOnly ? rowSelection : null}
            pagination={{
              position: ['bottomCenter'],
              showTotal: (total) => `Total ${total} items`,
              size:'small',
              defaultPageSize: props.readOnly ? 6 : 7,
              showSizeChanger: true,
              pageSizeOptions: [props.readOnly ? '6' : '7', '10', '20', '50'],
            }}
          />
        </Col>)}
      </Row>
      {cancelBtnTextAndOpenIabModal ?
        <AddIabChoicesModal
          cancelBtnTextAndOpenIabModal={cancelBtnTextAndOpenIabModal}
          handleCancel={handleCancel}
          addCategory={addIabChoice}
          systemPurposes={filteredSystemPurpose}
          isNoFrameworkFlow={props.isNoFrameworkFlow}
          disabledSellOrShare={disabledSellOrShare}
          stateStringRegions={stateStringRegions}
        />
        : null}
      {openModalCustom ?
        <AddCustomChoicesModal
          currentRecord={currentRecord}
          openCustomModal={openModalCustom}
          regulationCategoriesNames={regulationCategories.map(cat => cat.privacyChoice)}
          preDefinedChoiceNames={[...filteredSystemPurpose.map(a => a.privacyChoice), ...defaulStandardChoiceNames, ...props.customPrivacyChoiceOptions.map(a => a.privacyChoice)]}
          handleCancel={handleCancel}
          addCategory={addCustomChoice}
          editCustomChoice={editCustomChoice}
          isGlobalStandardFlow={props.isGlobalStandardFlow}
        />
        : null}
      {openGlobalStandardModal ?
        <AddChoicesModalForGlobalStandard
          openGlobalStandardModal={openGlobalStandardModal}
          regulationCategories={props.usPrivacyRegulation.categories}
          handleCancel={handleCancel}
          addStandardChoicesBulk={addStandardChoicesBulk}
          systemPurposes={filteredSystemPurpose}
          defaulStandardChoiceNames={defaulStandardChoiceNames.filter((choiceName) => !regulationCategories.map((cat) => cat.privacyChoice).includes(choiceName))}
          customPrivacyChoiceOptions={props.customPrivacyChoiceOptions.filter(({key}) => !regulationCategories.map(a => a.id).includes(key))}
          setCustomPrivacyChoiceOptions={props.setCustomPrivacyChoiceOptions}
        />
        : null}
        {reorderModalOpen ? (
        <ReorderModal
          open={reorderModalOpen}
          closeModal={() => setReorderModalOpen(false)}
          modalTitle = 'Reorder Choices'
          description = 'Drag and drop the privacy choices to display them in the order you would like them to appear in your message.'
          tableTitle = 'Privacy Choice'
          data = {regulationCategories}
          handleSaveReorder = {(data)=>setRegulationCategories(data)}
          dataIndex = 'privacyChoice'
          dataKey = 'privacyChoice'
        />
      ) : null}
        {contextHolder}
    </>
  );
}
export default ChoiceSelection;